import React from 'react';
import {
  AlertTriangle,
  Brain,
  Calendar,
  DollarSign,
  Gauge,
  Lightbulb,
  PieChart,
  Target,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import { Panel, StatCard, StatusPill, EmptyState } from '../ui';
import { formatCurrency, formatTrendLabel } from './utils';

const TrendBadge = ({ trend }) => {
  if (!trend) return null;
  const Icon =
    trend.direction === 'up'
      ? ArrowUpRight
      : trend.direction === 'down'
        ? ArrowDownRight
        : Minus;
  const color =
    trend.direction === 'up'
      ? 'text-green-600'
      : trend.direction === 'down'
        ? 'text-red-600'
        : 'text-secondary-500';
  return (
    <span className={`text-xs font-medium flex items-center gap-1 ${color}`}>
      <Icon className="h-3 w-3" />
      {formatTrendLabel(trend.deltaPct)}
    </span>
  );
};

const ExecutiveDashboard = ({
  kpis,
  projectMetrics = [],
  teamMetrics = [],
  trends = {},
  benchmarks,
  showPredictions,
  currencyCode = 'USD',
}) => {
  const atRisk = projectMetrics.filter((p) => (p.completionRate || 0) < 50).length;
  const topRevenueShare = (() => {
    const total = projectMetrics.reduce((s, p) => s + (p.revenue || 0), 0);
    if (!total) return 0;
    const top3 = projectMetrics.slice(0, 3).reduce((s, p) => s + (p.revenue || 0), 0);
    return (top3 / total) * 100;
  })();
  const overutilized = teamMetrics.filter((m) => (m.productivity || 0) > 40).length;
  const hoursTrend = trends.hours;
  const utilBenchmark = benchmarks?.utilization_rate;

  return (
    <div className="space-y-8" data-testid="executive-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="relative">
          <StatCard
            icon={DollarSign}
            tone="green"
            label="Total Revenue"
            value={formatCurrency(kpis.totalRevenue, currencyCode)}
          />
          <div className="absolute top-3 right-3">
            <TrendBadge trend={trends.revenue} />
          </div>
        </div>
        <div className="relative">
          <StatCard
            icon={Gauge}
            tone="blue"
            label="Team Utilization"
            value={`${(kpis.utilizationRate || 0).toFixed(1)}%`}
          />
          <div className="absolute top-3 right-3 text-xs text-blue-600 font-medium flex items-center gap-1">
            <Target className="h-3 w-3" />
            Target: {utilBenchmark?.top_performers || 85}%
          </div>
        </div>
        <div className="relative">
          <StatCard
            icon={TrendingUp}
            tone="purple"
            label="Billable Efficiency"
            value={`${(kpis.billablePercentage || 0).toFixed(1)}%`}
          />
          <div className="absolute top-3 right-3">
            <TrendBadge trend={trends.billable} />
          </div>
        </div>
        <div className="relative">
          <StatCard
            icon={Calendar}
            tone="amber"
            label="Active Projects"
            value={kpis.totalProjects}
          />
          <div className="absolute top-3 right-3 text-xs text-orange-600 font-medium">
            {atRisk} at risk
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel>
          <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 text-secondary-900">
            <Brain className="h-5 w-5 text-primary-600" />
            Strategic Insights
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
              <div className="flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">Revenue Concentration</h4>
                  <p className="text-sm text-blue-700 leading-relaxed">
                    Top 3 projects generate {topRevenueShare.toFixed(0)}% of revenue
                    {topRevenueShare > 70
                      ? '. Consider diversifying similar project types.'
                      : '.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
              <div className="flex items-start gap-3">
                <TrendingUp className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">Productivity Trend</h4>
                  <p className="text-sm text-green-700 leading-relaxed">
                    Hours logged are {formatTrendLabel(hoursTrend?.deltaPct)} vs the previous
                    period.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-1">Resource Alert</h4>
                  <p className="text-sm text-yellow-700 leading-relaxed">
                    {overutilized} team member{overutilized === 1 ? '' : 's'} logged over 40h this
                    period.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        <Panel>
          <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 text-secondary-900">
            <PieChart className="h-5 w-5 text-primary-600" />
            Project Performance
          </h3>
          {projectMetrics.length === 0 ? (
            <EmptyState title="No project data" description="Projects will appear once activity is logged." />
          ) : (
            <div className="space-y-3">
              {projectMetrics.slice(0, 5).map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-secondary-900 truncate">{project.name}</h4>
                    <p className="text-sm text-secondary-600">
                      {(project.completionRate || 0).toFixed(1)}% complete •{' '}
                      {formatCurrency(project.revenue, currencyCode)}
                    </p>
                  </div>
                  <div className="text-right ml-4 space-y-1">
                    <StatusPill
                      status={
                        project.completionRate >= 80
                          ? 'Done'
                          : project.completionRate >= 50
                            ? 'At Risk'
                            : 'Blocked'
                      }
                      label={`${(project.completionRate || 0).toFixed(1)}%`}
                    />
                    <div className="text-xs text-secondary-500">
                      {project.overdueTasks || 0} overdue
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {showPredictions && (
        <Panel>
          <h3 className="text-lg font-semibold mb-6 text-secondary-900">Forward Look</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Projected Revenue</h4>
              <p className="text-3xl font-bold text-blue-600 mb-1">
                {formatCurrency(
                  (kpis.totalRevenue || 0) *
                    (1 + Math.max(0, (trends.revenue?.deltaPct || 0) / 100)),
                  currencyCode
                )}
              </p>
              <p className="text-sm text-blue-700">Based on current trend</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">Utilization</h4>
              <p className="text-3xl font-bold text-green-600 mb-1">
                {(kpis.utilizationRate || 0).toFixed(0)}%
              </p>
              <p className="text-sm text-green-700">
                Industry avg {utilBenchmark?.industry_average || 75}%
              </p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2">On Track Projects</h4>
              <p className="text-3xl font-bold text-purple-600 mb-1">
                {projectMetrics.filter((p) => (p.completionRate || 0) >= 80).length}
              </p>
              <p className="text-sm text-purple-700">of {projectMetrics.length} active</p>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
};

export default ExecutiveDashboard;
